import React, { Suspense } from 'react';
import { RiBookOpenLine } from '@remixicon/react';
import {
  type IconComponent,
  createFrontendModule,
  createExtensionInput,
  createApiRef,
  PageBlueprint,
  NavItemBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  AddonBlueprint,
  attachTechDocsAddonComponentData,
} from '@backstage/plugin-techdocs-react/alpha';
import {
  TechDocsAddons,
  type TechDocsAddonOptions,
} from '@backstage/plugin-techdocs-react';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';

// Recreated locally — the original ref is not exported from @backstage/plugin-techdocs/alpha.
// The ApiHolder resolves by id, so matching 'plugin.techdocs.addons' is sufficient.
const techdocsAddonsApiRef = createApiRef<{
  getAddons(): TechDocsAddonOptions[];
}>({ id: 'plugin.techdocs.addons' });

export const docsPagesModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/docs',
        routeRef: techdocsPlugin.routes.root,
        noHeader: true,
        loader: () =>
          import('./docs.page').then(m => React.createElement(m.DocsPage)),
      },
    }),
    PageBlueprint.makeWithOverrides({
      name: 'reader',
      inputs: {
        addons: createExtensionInput([AddonBlueprint.dataRefs.addon]),
      },
      factory(originalFactory, { apis, inputs }) {
        const addonsApi = apis.get(techdocsAddonsApiRef);
        return originalFactory({
          path: '/docs/:namespace/:kind/:name',
          routeRef: techdocsPlugin.routes.docRoot,
          noHeader: true,
          loader: () => {
            const apiAddons = addonsApi?.getAddons() ?? [];
            const directAddons = inputs.addons.map(output =>
              output.get(AddonBlueprint.dataRefs.addon),
            );
            const allAddons = [...apiAddons, ...directAddons];
            const addonElements = allAddons.map(options => {
              const Addon = options.component;
              attachTechDocsAddonComponentData(Addon, options);
              return React.createElement(
                Suspense,
                { fallback: null, key: options.name },
                React.createElement(Addon),
              );
            });
            const addonsNode = addonElements.length
              ? React.createElement(TechDocsAddons, null, ...addonElements)
              : undefined;
            return import('./techdocs-reader.page').then(m =>
              React.createElement(m.TechDocsReaderPage, {
                addons: addonsNode,
              }),
            );
          },
        });
      },
    }),
    NavItemBlueprint.make({
      params: {
        title: 'Docs',
        icon: RiBookOpenLine as IconComponent,
        routeRef: techdocsPlugin.routes.root,
      },
    }),
  ],
});
